// Uniform line: garments, sectors, logo positions and the price rule. One
// file, copied byte-for-byte into the admin panel
// (admin/src/Data/uniformCatalog.js) and generated as
// api/_lib/uniformCatalog.cjs for the order API; run
// `node scripts/check-taxonomy-sync.js --write` after editing it.
// Spec: docs/uniform-plan.md. No imports, so every copy works as-is.

export const UNIFORM_CATEGORY = "uniform";
export const DEFAULT_BULK_FROM = 50;
export const TEAM_FROM = 10;

// Sector keys. Pages live in the taxonomy (uniform department); the last
// three are filter-only and roll up into the page named in `parent`.
export const UNIFORM_SECTORS = [
  { key: "hotel", label: "Hotel" },
  { key: "restaurant", label: "Restaurant & Cafe" },
  { key: "hospital", label: "Hospital & Clinic" },
  { key: "corporate", label: "Corporate & Office" },
  { key: "school", label: "School & College" },
  { key: "factory", label: "Factory & Industrial" },
  { key: "security", label: "Security" },
  { key: "spa", label: "Salon & Spa" },
  { key: "event", label: "Event & Catering", parent: "hotel" },
  { key: "bank", label: "Bank & Showroom", parent: "corporate" },
  { key: "transport", label: "Driver & Transport", parent: "corporate" },
];

// Where a logo can go, with the size (inches) the admin form starts from.
export const LOGO_POSITIONS = [
  { key: "left_chest", label: "Left chest", sizeIn: 3 },
  { key: "right_chest", label: "Right chest", sizeIn: 3 },
  { key: "back", label: "Back", sizeIn: 10 },
  { key: "back_neck", label: "Back of the neck", sizeIn: 2.5 },
  { key: "left_sleeve", label: "Left sleeve", sizeIn: 2.5 },
  { key: "right_sleeve", label: "Right sleeve", sizeIn: 2.5 },
  { key: "cap_front", label: "Cap front", sizeIn: 2.5 },
  { key: "apron_front", label: "Apron front", sizeIn: 4 },
  { key: "collar", label: "Collar", sizeIn: 1.5 },
];

// A logo row's size in inches. Rows saved before inches kept centimetres.
export const logoSizeIn = (row) => {
  const inches = Number(row && row.sizeIn);
  if (inches > 0) return inches;
  const cm = Number(row && row.sizeCm);
  if (cm > 0) return Math.round((cm / 2.54) * 2) / 2;
  return (positionByKey(row && row.position) || {}).sizeIn || 0;
};

export const LOGO_METHODS = [
  {
    key: "embroidery",
    label: "Embroidery",
    pros: ["Premium, slightly raised finish", "Lasts as long as the garment", "Colours never fade"],
    cons: ["Up to 6–8 colours, no gradients", "Very fine text does not stitch cleanly"],
  },
  {
    key: "print",
    label: "Print",
    pros: ["Any design, full colour and gradients", "Flat and soft on the fabric", "Costs less"],
    cons: ["Can fade after 40–60 washes", "Not for textured fabrics such as wool blazers"],
  },
];

// Where a staff name can be stitched or printed: right under the logo, or at
// any logo position.
export const NAME_PLACES = [
  { key: "below_logo", label: "Below the logo" },
  ...LOGO_POSITIONS.map((p) => ({ key: p.key, label: p.label })),
];
export const namePlaceLabel = (key) => (NAME_PLACES.find((p) => p.key === key) || NAME_PLACES[0]).label;

export const NAME_COLORS = [
  { key: "white", label: "White", hex: "#ffffff" },
  { key: "black", label: "Black", hex: "#141414" },
  { key: "gold", label: "Gold", hex: "#c9a14a" },
  { key: "navy", label: "Navy", hex: "#1d2b4f" },
];

// Same sizes as the size chart (src/Data/sizing.js).
const TOPS = ["XS", "S", "M", "L", "XL", "XXL", "3XL", "4XL", "5XL", "6XL"];
const WAIST = ["28", "30", "32", "34", "36", "38", "40", "42", "44", "46"];
const FREE = ["Free size"];
export const SIZE_RUNS = { tops: TOPS, waist: WAIST, free: FREE };
export const SIZE_RUN_OPTIONS = [
  { key: "tops", label: "Tops, by chest", range: "XS–6XL" },
  { key: "waist", label: "Trousers, by waist", range: "28–46" },
  { key: "free", label: "Free size", range: "One size" },
];

// Maviinci stitches every size, so a uniform is sold in its whole size run;
// the product only says which run. Older products saved a hand-picked list,
// which still tells the run apart (numbers = waist).
export function sizeRunOf(item, garmentKey) {
  if (item && SIZE_RUNS[item.sizeRun]) return item.sizeRun;
  const list = (item && Array.isArray(item.sizes) ? item.sizes : []).map(String);
  if (list.length) {
    const graded = list.filter((z) => !/free/i.test(z));
    if (!graded.length) return "free";
    return graded.every((z) => /^\d+$/.test(z)) ? "waist" : "tops";
  }
  return (garmentByKey(garmentKey) || {}).sizeRun || "tops";
}

/**
 * A uniform's library fabrics in the shape other products keep in `fabrics`
 * ({ type, id, name, src, ... }), so the product page's fabric cards and
 * Fabric Details work the same. pieceColor is the colour this uniform uses,
 * which wins over the full colour list the fabric master brings.
 */
export function uniformFabrics(product) {
  if (!product) return [];
  const one = (lib, x, type) => (lib && (lib.id || lib.name)
    ? {
      type,
      id: lib.id || "",
      fabricID: lib.id || "",
      name: lib.name || "",
      fabricName: lib.name || "",
      brand: lib.brand || "",
      src: lib.src || "",
      material: lib.material || x.material || "",
      color: x.colorName || lib.color || "",
      pieceColor: x.colorName || "",
    }
    : null);
  if (Array.isArray(product.setPieces) && product.setPieces.length) {
    return product.setPieces.map((p) => one(p.libraryFabric, p, pieceLabel(p.key))).filter(Boolean);
  }
  return [one(product.libraryFabric, product, "Fabric")].filter(Boolean);
}

/** The product with every piece offering its full size run. */
export function withFullSizes(product) {
  if (!product) return product;
  const out = { ...product, sizes: SIZE_RUNS[sizeRunOf(product, product.garment)] };
  if (Array.isArray(product.setPieces)) {
    out.setPieces = product.setPieces.map((p) => ({ ...p, sizes: SIZE_RUNS[sizeRunOf(p, p.key)] }));
  }
  return out;
}

// g: group · s: sectors it is mainly worn in · p: default logo positions ·
// z: size run · kit: a garment that is really two pieces (scrub suit = top +
// pant); picking it makes the uniform a set of those pieces, each with its
// own size (top by chest, pant by waist).
const G = (key, label, g, s, p, z, kit) => ({ key, label, group: g, defaultSectors: s, logoPositions: p, sizeRun: z, ...(kit ? { kit } : {}) });
export const UNIFORM_GROUPS = [
  { key: "formal", label: "Formal & Office" },
  { key: "ethnic", label: "Designer & Ceremonial" },
  { key: "team", label: "Team & Casual" },
  { key: "kitchen", label: "Kitchen & Chef" },
  { key: "house", label: "Housekeeping, Spa & Salon" },
  { key: "medical", label: "Medical" },
  { key: "industrial", label: "Factory & Safety" },
  { key: "security", label: "Security" },
  { key: "school", label: "School" },
];
export const UNIFORM_GARMENTS = [
  G("formal_shirt", "Formal shirt", "formal", ["hotel", "corporate", "bank", "school", "security", "transport"], ["left_chest"], "tops"),
  G("formal_trouser", "Formal trouser", "formal", ["hotel", "restaurant", "corporate", "bank", "school", "security", "event", "transport"], [], "waist"),
  G("blazer", "Blazer", "formal", ["hotel", "corporate", "bank", "school"], ["left_chest"], "tops"),
  G("suit", "Suit (2 / 3 piece)", "formal", ["hotel", "corporate"], ["left_chest"], "tops", ["blazer", "formal_trouser"]),
  G("waistcoat", "Waistcoat", "formal", ["hotel", "restaurant", "event"], ["left_chest"], "tops"),
  G("skirt", "Skirt / ladies trouser", "formal", ["school"], [], "waist"),
  G("tie", "Tie", "formal", ["hotel", "corporate", "bank", "school", "security"], [], "free"),
  G("bow_tie", "Bow tie", "formal", ["hotel", "restaurant", "event"], [], "free"),
  G("scarf", "Scarf / stole", "formal", ["bank"], [], "free"),
  G("safari_suit", "Safari suit", "formal", ["transport"], ["left_chest"], "tops", ["safari_jacket", "formal_trouser"]),
  G("safari_jacket", "Safari shirt-jacket", "formal", ["transport"], ["left_chest"], "tops"),

  G("bandhgala", "Bandhgala / Jodhpuri coat", "ethnic", ["hotel", "event"], ["left_chest"], "tops"),
  G("doorman_coat", "Doorman coat / Achkan", "ethnic", ["hotel"], ["left_chest"], "tops"),
  G("staff_kurta", "Staff kurta", "ethnic", ["hotel", "restaurant", "spa"], ["left_chest"], "tops"),
  G("nehru_jacket", "Nehru jacket / Sadri", "ethnic", ["event"], ["left_chest"], "tops"),
  G("safa", "Safa / Pagdi", "ethnic", ["hotel", "event"], [], "free"),
  G("kamarbandh", "Kamarbandh / Sash", "ethnic", ["hotel"], [], "free"),

  G("polo", "Polo T-shirt", "team", ["restaurant", "factory", "event"], ["left_chest", "back"], "tops"),
  G("tshirt", "Round-neck T-shirt", "team", ["event"], ["left_chest", "back"], "tops"),
  G("sweatshirt", "Sweatshirt / Hoodie", "team", [], ["left_chest", "back"], "tops"),
  G("sweater", "Sweater / Cardigan", "team", ["school"], ["left_chest"], "tops"),
  G("winter_jacket", "Winter jacket", "team", ["factory", "security"], ["left_chest", "back"], "tops"),
  G("cap", "Cap", "team", [], ["cap_front"], "free"),

  G("chef_coat", "Chef coat", "kitchen", ["hotel", "restaurant", "event"], ["left_chest"], "tops"),
  G("chef_pant", "Chef pant", "kitchen", ["hotel", "restaurant"], [], "waist"),
  G("chef_cap", "Chef cap / Skull cap", "kitchen", ["hotel", "restaurant"], ["cap_front"], "free"),
  G("neckerchief", "Neckerchief", "kitchen", [], [], "free"),
  G("bib_apron", "Kitchen / Bib apron", "kitchen", ["hotel", "restaurant", "event"], ["apron_front"], "free"),
  G("waist_apron", "Bistro / Waist apron", "kitchen", ["hotel", "restaurant", "event"], ["apron_front"], "free"),
  G("bar_apron", "Bar apron", "kitchen", ["restaurant"], ["apron_front"], "free"),
  G("utility_apron", "Waterproof / Utility apron", "kitchen", [], ["apron_front"], "free"),

  G("hk_tunic", "Housekeeping tunic", "house", ["hotel", "hospital"], ["left_chest"], "tops"),
  G("hk_apron", "Housekeeping apron", "house", ["hotel"], ["apron_front"], "free"),
  G("spa_set", "Spa tunic + trouser", "house", ["spa"], ["left_chest"], "tops", ["spa_tunic", "spa_trouser"]),
  G("spa_tunic", "Spa tunic", "house", ["spa"], ["left_chest"], "tops"),
  G("spa_trouser", "Spa trouser", "house", ["spa"], [], "waist"),
  G("salon_apron", "Salon apron / Cape", "house", ["spa"], ["apron_front"], "free"),

  G("scrub_suit", "Scrub suit", "medical", ["hospital"], ["left_chest"], "tops", ["scrub_top", "scrub_pant"]),
  G("scrub_top", "Scrub top", "medical", ["hospital"], ["left_chest"], "tops"),
  G("scrub_pant", "Scrub pant", "medical", ["hospital"], [], "waist"),
  G("lab_coat", "Lab coat / Doctor coat", "medical", ["hospital", "factory"], ["left_chest"], "tops"),
  G("nurse_uniform", "Nurse uniform", "medical", ["hospital"], ["left_chest"], "tops"),
  G("ot_gown", "OT / Patient gown", "medical", ["hospital"], [], "free"),
  G("scrub_cap", "Scrub cap", "medical", ["hospital"], [], "free"),

  G("coverall", "Coverall / Boiler suit", "industrial", ["factory"], ["left_chest", "back"], "tops"),
  G("work_shirt", "Work shirt", "industrial", ["factory"], ["left_chest"], "tops"),
  G("cargo_trouser", "Cargo trouser", "industrial", ["factory"], [], "waist"),
  G("hivis_jacket", "Hi-vis safety jacket", "industrial", ["factory", "transport"], ["left_chest", "back"], "tops"),
  G("fr_coverall", "FR coverall", "industrial", ["factory"], ["left_chest", "back"], "tops"),
  G("welding_apron", "Leather welding apron", "industrial", ["factory"], [], "free"),
  G("cleanroom_suit", "Cleanroom suit / Hair cap", "industrial", ["factory"], [], "tops"),

  G("security_set", "Security shirt + trouser set", "security", ["security"], ["left_chest", "left_sleeve"], "tops", ["security_shirt", "security_trouser"]),
  G("security_shirt", "Security shirt", "security", ["security"], ["left_chest", "left_sleeve"], "tops"),
  G("security_trouser", "Security trouser", "security", ["security"], [], "waist"),
  G("security_cap", "Security beret / Peak cap", "security", ["security"], ["cap_front"], "free"),
  G("security_belt", "Belt / Lanyard", "security", ["security"], [], "free"),

  G("pinafore", "Pinafore dress", "school", ["school"], ["left_chest"], "tops"),
  G("salwar_kurta_set", "Salwar-kurta set", "school", ["school"], ["left_chest"], "tops", ["school_kurta", "salwar"]),
  G("school_kurta", "School kurta", "school", ["school"], ["left_chest"], "tops"),
  G("salwar", "Salwar", "school", ["school"], [], "waist"),
  G("tracksuit", "Track suit", "school", ["school"], ["left_chest"], "tops", ["track_jacket", "track_pant"]),
  G("track_jacket", "Track jacket", "school", ["school"], ["left_chest"], "tops"),
  G("track_pant", "Track pant", "school", ["school"], [], "waist"),
];

const list = (v) => (Array.isArray(v) ? v : v == null || v === "" ? [] : [v]);
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export const garmentByKey = (key) => UNIFORM_GARMENTS.find((g) => g.key === key) || null;
// Sleeve type, which decides the sleeve measurements: full (shoulder to
// wrist), half (shoulder to mid-bicep), or none. The product or a piece can
// override it (a half-sleeve security shirt).
export const SLEEVE_OPTIONS = [
  { key: "full", label: "Full sleeve" },
  { key: "half", label: "Half sleeve" },
  { key: "none", label: "Sleeveless" },
];
const HALF_SLEEVE = ["scrub_top", "scrub_suit", "polo", "tshirt", "hk_tunic", "spa_tunic", "spa_set", "nurse_uniform"];
const NO_SLEEVE = /waistcoat|apron|bow_tie|^tie$|scarf|cap|safa|kamarbandh|belt|neckerchief|trouser|pant|skirt|salwar|ot_gown|cleanroom/;
export function defaultSleeve(garmentKey) {
  const k = String(garmentKey || "");
  if (HALF_SLEEVE.includes(k)) return "half";
  if (NO_SLEEVE.test(k)) return "none";
  return "full";
}
/** The sleeve type of a product or a set piece. */
export const sleeveOf = (item, garmentKey) =>
  (item && SLEEVE_OPTIONS.some((o) => o.key === item.sleeve) ? item.sleeve : defaultSleeve(garmentKey || (item && (item.key || item.garment))));

/** The pieces a two-piece garment is made of, or null for a single garment. */
export const kitOf = (key) => (garmentByKey(key) || {}).kit || null;
export const positionByKey = (key) => LOGO_POSITIONS.find((p) => p.key === key) || null;
export const sectorByKey = (key) => UNIFORM_SECTORS.find((s) => s.key === key) || null;
export const isUniformDoc = (doc) => doc?.productKind === "uniform" || doc?.category === UNIFORM_CATEGORY;

// ---- sets (shirt + trouser, chef coat + pant + cap …) ----
// A set product lists its pieces in `setPieces`: [{ key, sizes, price1,
// price10 }]. price1/price10 on the product are the full-set prices; a piece
// with its own prices can also be bought alone. Logo rows and staff names
// name the piece they sit on (`piece`).
export const SET_GARMENT = "set";
export const isSetProduct = (product) => Array.isArray(product?.setPieces) && product.setPieces.length > 0;
export const pieceLabel = (key) => garmentByKey(key)?.label || key;
export function uniformLabel(product) {
  if (isSetProduct(product)) return product.setPieces.map((p) => pieceLabel(p.key)).join(" + ");
  return garmentByKey(product?.garment)?.label || "Uniform";
}
// Ways a set can be bought: the full set, plus each piece that has a price.
export function buyModes(product) {
  if (!isSetProduct(product)) return [{ key: "set", label: uniformLabel(product) }];
  return [
    { key: "set", label: `Full set (${uniformLabel(product)})` },
    ...product.setPieces.filter((p) => num(p.price1) > 0).map((p) => ({ key: p.key, label: `Only ${pieceLabel(p.key).toLowerCase()}` })),
  ];
}
const inMode = (mode, piece) => !piece || !mode || mode === "set" || mode === piece;

// A product's logo rows with only the methods it actually offers. `key` is
// the row id the buy panel and the cart use (piece:position on a set).
// A uniform sold plain (the admin switched "takes a logo" off) offers none.
export const takesLogo = (product) => product?.logoEnabled !== false;

export function offeredLogoRows(product, mode) {
  if (!takesLogo(product)) return [];
  return list(product?.logoPositions)
    .map((row) => {
      const methods = {};
      LOGO_METHODS.forEach((m) => {
        const r = row?.methods?.[m.key];
        if (r && num(r.perPiece) > 0) methods[m.key] = { perPiece: num(r.perPiece), setup: num(r.setup) };
      });
      const keys = Object.keys(methods);
      if (!row?.position || !keys.length) return null;
      const posLabel = positionByKey(row.position)?.label || row.position;
      return {
        key: row.piece ? `${row.piece}:${row.position}` : row.position,
        position: row.position,
        piece: row.piece || "",
        label: row.piece && isSetProduct(product) ? `${pieceLabel(row.piece)} · ${posLabel}` : posLabel,
        sizeIn: logoSizeIn(row),
        methods,
        defaultMethod: methods[row.defaultMethod] ? row.defaultMethod : keys[0],
      };
    })
    .filter(Boolean)
    .filter((row) => inMode(mode, row.piece));
}

export const staffNamesOf = (product, mode) => {
  const s = product?.staffNames || {};
  if (!s.enabled || !num(s.perPiece)) return null;
  if (!inMode(mode, s.piece)) return null;
  return {
    position: s.position || "right_chest",
    positionLabel: namePlaceLabel(s.position || "right_chest"),
    piece: s.piece || "",
    perPiece: num(s.perPiece),
    maxChars: num(s.maxChars) || 20,
    colors: list(s.colors).length ? list(s.colors) : NAME_COLORS.map((c) => c.key),
  };
};

/**
 * The price of a uniform order line. The product page shows it live; the
 * order API recomputes it from the product document and never trusts the
 * client's figure.
 *
 * @param product  Presets document (price1, price10, bulkFrom, logoPositions,
 *                 staffNames, setPieces)
 * @param cfg      single garment: { sizes: [{ size, qty, names }] }
 *                 set: { mode: "set" | pieceKey, rows: [{ sizes: { pieceKey: size }, qty, names }] }
 *                 both: { logos: [{ key | position, on, method, logoId }], namesOn }
 */
export function uniformQuote(product, cfg) {
  const isSet = isSetProduct(product);
  const mode = isSet ? (cfg?.mode && cfg.mode !== "set" && product.setPieces.some((p) => p.key === cfg.mode && num(p.price1) > 0) ? cfg.mode : "set") : "set";
  const piece = isSet && mode !== "set" ? product.setPieces.find((p) => p.key === mode) : null;
  const sizes = (isSet
    ? list(cfg?.rows).map((r) => ({
        // One-size pieces (apron, bow tie) say nothing about the person.
        size: (mode === "set" ? product.setPieces.map((p) => r?.sizes?.[p.key]) : [r?.sizes?.[mode]]).filter((z) => z && !/free/i.test(z)).join(" / ") || "One size",
        qty: r?.qty,
        names: r?.names,
      }))
    : list(cfg?.sizes)
  ).map((x) => ({ ...x, qty: Math.max(0, Math.floor(num(x?.qty))) }));
  const qty = sizes.reduce((sum, x) => sum + x.qty, 0);
  const price1 = piece ? num(piece.price1) : num(product?.price1) || num(product?.priceFinal) || num(product?.price);
  const price10 = (piece ? num(piece.price10) : num(product?.price10)) || price1;
  const bulkFrom = num(product?.bulkFrom) || DEFAULT_BULK_FROM;
  const tier = qty >= bulkFrom ? "bulk" : qty >= TEAM_FROM ? "team" : "single";
  const unitPrice = tier === "single" ? price1 : price10;
  const garment = unitPrice * qty;

  const rows = offeredLogoRows(product, mode);
  const chosen = list(cfg?.logos);
  const logoLines = [];
  const setups = new Map();
  rows.forEach((row) => {
    const pick = chosen.find((c) => c && (c.key ? c.key === row.key : c.position === row.position && !row.piece));
    if (!pick || !pick.on) return;
    const method = row.methods[pick.method] ? pick.method : row.defaultMethod;
    const m = row.methods[method];
    logoLines.push({
      key: row.key,
      position: row.position,
      piece: row.piece,
      label: row.label,
      method,
      sizeIn: row.sizeIn,
      perPiece: m.perPiece,
      qty,
      amount: m.perPiece * qty,
    });
    // One setup per logo + method + size: the same die serves every
    // position that shares all three.
    const key = `${pick.logoId || "logo"}|${method}|${row.sizeIn}`;
    const prev = setups.get(key);
    if (!prev) setups.set(key, { key, method, sizeIn: row.sizeIn, amount: m.setup, positions: [row.label] });
    else prev.positions.push(row.label);
  });
  // Nothing ordered yet, nothing to set up.
  const setupList = qty > 0 ? [...setups.values()].filter((x) => x.amount > 0) : [];
  const logo = logoLines.reduce((sum, l) => sum + l.amount, 0);
  const setup = setupList.reduce((sum, x) => sum + x.amount, 0);

  const nameRule = staffNamesOf(product, mode);
  const named = nameRule && cfg?.namesOn
    ? sizes.reduce((sum, x) => sum + list(x.names).slice(0, x.qty).filter((n) => String(n || "").trim()).length, 0)
    : 0;
  const names = named * (nameRule ? nameRule.perPiece : 0);

  const total = garment + logo + setup + names;
  return {
    qty,
    mode,
    tier,
    bulk: tier === "bulk",
    bulkFrom,
    unitPrice,
    price1,
    price10,
    garment,
    lines: sizes.filter((x) => x.qty),
    logoLines,
    setups: setupList,
    logo,
    setup,
    named,
    names,
    total,
    perPiece: qty ? Math.round(total / qty) : 0,
  };
}
